import { useEffect, useState } from 'react';
import { Box, Button, Link, Spinner, Text, Title } from '@nimbus-ds/components';
import { SearchIcon } from '@nimbus-ds/icons';
import {
  CheckoutPaymentOptionDto,
  SortTypePaymentOrder,
} from '@tiendanube/common/src/domains/checkout/CheckoutPaymentsResponseDto';
import { CheckoutSavePaymentOrdersCustomDto } from '@tiendanube/common/src/domains/checkout/CheckoutSavePaymentOrdersDto';
import HelpLink from 'App/HelpLink';
import { ErrorScreen } from 'commons/components';
import { SORT_PAYMENT_METHODS_TUTORIAL_LINK } from 'commons/constants';
import { useToastStatus, useTranslationLanguage } from 'commons/hooks';
import {
  useFetchCheckoutPaymentOptions,
  useSaveCheckoutPaymentOptionsOrder,
  useHasCheckoutPaymentOrderChanged,
} from 'domains/Payments/PaymentProviders/hooks';
import useTranslationPayments from 'domains/Payments/useTranslationPayments';
import Skeleton from './Skeleton';
import CheckoutPaymentOrderType from '../CheckoutPaymentOrderType';
import SortCheckoutPaymentsListCustom from '../SortCheckoutPaymentsListCustom';
import SortCheckoutPaymentsListDefault from '../SortCheckoutPaymentsListDefault';

interface SortPaymentsModalContentProps {
  onClose: () => void;
}

const DEFAULT_SORT_TYPE = 'default';
const CUSTOM_SORT_TYPE = 'custom';

export function SortPaymentsModalContent({
  onClose,
}: SortPaymentsModalContentProps) {
  const t = useTranslationPayments('sortPaymentsModal');

  const {
    mutation,
    isLoading: isLoadingMutation,
    data: dataMutation,
  } = useSaveCheckoutPaymentOptionsOrder();
  const {
    getCheckoutOptions,
    isLoading: isLoadingFetch,
    isError: isErrorFetch,
    data: dataFetch,
  } = useFetchCheckoutPaymentOptions();

  const language = useTranslationLanguage();
  const [sortType, setSortType] = useState<SortTypePaymentOrder>();
  const [customData, setCustomData] = useState<CheckoutPaymentOptionDto[]>([]);
  const [defaultData, setDefaultData] = useState<CheckoutPaymentOptionDto[]>(
    [],
  );
  const [onlyMobileOptions, setOnlyMobileOptions] = useState<
    CheckoutPaymentOptionDto[]
  >([]);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [isChangingToDefaultType, setIsChangingToDefaultType] = useState(false);

  useEffect(() => {
    getCheckoutOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dataFetch?.sortType === CUSTOM_SORT_TYPE) {
      setSortType(CUSTOM_SORT_TYPE);
    }
  }, [dataFetch]);

  useEffect(() => {
    if (dataFetch) {
      setSortType(dataFetch.sortType);
      const onlyMobileOptions = dataFetch.custom.filter(
        (option) => option.displayOnlyMobile,
      );
      const customOptions = dataFetch.custom.filter(
        (option) => !option.displayOnlyMobile,
      );
      const defaultOptions = dataFetch.default.filter(
        (option) => !option.displayOnlyMobile,
      );
      setCustomData(customOptions);
      setDefaultData(defaultOptions);
      setOnlyMobileOptions(onlyMobileOptions);
    }
  }, [dataFetch]);

  useToastStatus({
    success: t('textSuccess'),
    error: t('textError'),
    status: dataMutation,
  });

  const disabledSaveButton = useHasCheckoutPaymentOrderChanged(dataFetch, {
    sortType: sortType || DEFAULT_SORT_TYPE,
    custom: customData,
    default: defaultData,
  });

  useEffect(() => {
    if (dataMutation === 'success') {
      setIsChangingToDefaultType(false);
      if (shouldCloseModal) {
        onClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMutation, onClose]);

  const handleSortType = (item) => {
    if (item.value === DEFAULT_SORT_TYPE) {
      setSortType(DEFAULT_SORT_TYPE);
    } else {
      setSortType(CUSTOM_SORT_TYPE);
    }
  };

  const handleCancelButton = () => {
    onClose();
  };

  const handleSaveButton = () => {
    if (!sortType) return;

    let displayPriority = 0;
    const custom: CheckoutSavePaymentOrdersCustomDto[] = [];
    if (sortType === CUSTOM_SORT_TYPE) {
      customData.forEach((item) => {
        if (!('fixedPriority' in item)) {
          custom.push({
            displayPriority: ++displayPriority,
            appId: item?.appId,
            paymentOptionId: item.id,
            integrationType: item.integrationType,
            method: item.method,
            gatewayId: item?.gatewayId,
          });
        }
      });

      // Adds payment options that are displayed only
      // on mobile at the end of the list with displayPriority 0
      if (onlyMobileOptions.length > 0) {
        onlyMobileOptions.forEach((option) => {
          custom.push({
            displayPriority: 0,
            appId: option?.appId,
            paymentOptionId: option.id,
            integrationType: option.integrationType,
            method: option.method,
            gatewayId: option?.gatewayId,
          });
        });
      }
    }

    setShouldCloseModal(true);
    mutation({
      sortType: sortType,
      custom,
    });
  };

  const handleOnChange = (data: CheckoutPaymentOptionDto[]) => {
    setCustomData(data);
  };

  if (isErrorFetch) {
    return (
      <>
        <Text>{t('textBody')}</Text>
        <ErrorScreen
          description={t('errorText')}
          onRetry={getCheckoutOptions}
        />
      </>
    );
  }

  if (!dataFetch || isLoadingFetch || isChangingToDefaultType) {
    return (
      <>
        <Text>{t('textBody')}</Text>
        <Skeleton />
      </>
    );
  }

  if (dataFetch && !dataFetch.default.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap="2"
        alignItems="center"
        alignSelf="center"
        maxWidth="80%"
      >
        <SearchIcon size="large" />
        <Title textAlign="center" as="h4">
          {t('emptyText')}
        </Title>
        <Link onClick={handleCancelButton}>{t('emptyActionText')}</Link>
      </Box>
    );
  }

  if (!dataFetch) return null;

  return (
    <>
      <Text>{t('textBody')}</Text>
      <CheckoutPaymentOrderType value={sortType} onChange={handleSortType} />
      {sortType === DEFAULT_SORT_TYPE && (
        <SortCheckoutPaymentsListDefault data={defaultData} />
      )}
      {sortType === CUSTOM_SORT_TYPE && (
        <SortCheckoutPaymentsListCustom
          onChange={handleOnChange}
          data={customData}
        />
      )}
      <Box display="flex" justifyContent="flex-end" gap="2">
        <Button onClick={handleCancelButton}>{t('cancel')}</Button>
        <Button
          disabled={isLoadingMutation || disabledSaveButton}
          onClick={handleSaveButton}
          appearance="primary"
        >
          {isLoadingMutation && <Spinner color="currentColor" size="small" />}
          {t('save')}
        </Button>
      </Box>
      <HelpLink
        title={t('helpLink.title')}
        previousValue=""
        currentViewTracking=""
        linkURL={SORT_PAYMENT_METHODS_TUTORIAL_LINK[language]}
        showInMobile
        icon="both"
      />
    </>
  );

  return null;
}
